/**
 * Yoga activation — Vol_07. A life event of success/wealth should not merely have a
 * house/dasha/varga connection; the relevant YOGA should be (1) FORMED in the chart,
 * (2) STRONG, and (3) ACTIVATED by the running dasha (Vol_07 §1.2 — the three requirements
 * for a yoga to manifest; §1.3 grading). This is exactly the gap PLAN.md §5b tracked.
 *
 * Why it discriminates birth time: yoga FORMATION depends on which planets lord the Kendras
 * (4/7/10) and Trikonas (1/5/9) — i.e. on the rising sign, which changes every ~2 h — and
 * ACTIVATION depends on the Vimshottari dasha running at the event date (birth-time-dependent).
 * So a candidate lagna under which the event-category's yoga is both formed and dasha-active
 * scores higher. Like transit/gochar it is a LIGHT CONFIRMATION only (50–100, never penalises):
 * a candidate that doesn't form the yoga simply gets no yoga component, not a penalty.
 *
 * Scope (where Vol_07 gives an unambiguous event→yoga mapping):
 *   - Raja Yoga (§2.1 Kendra–Trikona lord link; §2.2 Yoga Karaka) → power/status/career rise.
 *   - Dhana Yoga (§3.1 wealth-house 2/5/9/11 lord links + the single-lord placement forms of
 *     07_dhana_yogas_wealth_poverty.md) → wealth/award.
 *   - Panchamahapurusha (07_panchamahapurusha_yogas.md: Mars/Mercury/Jupiter/Venus/Saturn in
 *     own/exaltation sign AND in a Kendra from the Lagna) → status/greatness events. Kendra
 *     membership hangs on the rising sign, so this route too discriminates birth time.
 * Events outside this scope return null (no yoga component).
 *
 * Strength honours the FULL 07_yoga_fundamentals_and_cancellation.md grading: yoga planets must
 * be "free from debilitation, combustion, or planetary war"; "Combust, debilitated, or defeated
 * in planetary war: Diminished to nil results" — so a combust or war-defeated yoga planet is
 * dropped to the debilitated floor. Yoga Bhanga (chart-wide cancellation) forces the whole
 * component to the neutral 50 — never below, the component stays never-penalising.
 */

import { SIGN_RULERS } from "../astro/constants";
import {
  compute_combustion,
  compute_dignity,
  compute_graha_yuddha,
  deep_exaltation_longitude,
  get_drishti_strength,
  is_vargottama,
  natural_relation,
} from "../astro/helpers";
import { get_current_dasha } from "../astro/dasha";
import { get_house_from_signs } from "../astro/chart";
import { get_divisional_position } from "../astro/transit_data";
import type { PlainDateTime } from "./datetime";
import { pythonRound } from "./pyutil";

const PLANETS = ["sun", "moon", "mars", "mercury", "jupiter", "venus", "saturn"];

// §2.1: Kendras are 4/7/10; Trikonas are 1/5/9 (the 1st counts as a trikona — "5th, 9th, and
// also the 1st which is both"). A Raja Yoga links a pure-Kendra lord with a Trikona lord.
const KENDRA_HOUSES = [4, 7, 10];
const TRIKONA_HOUSES = [1, 5, 9];
// For node/PMP placement "in a Kendra", the full kendra set including the 1st (07_raja_yogas.md,
// 07_panchamahapurusha_yogas.md: "1st, 4th, 7th, or 10th house").
const KENDRA_HOUSES_FULL = [1, 4, 7, 10];
// Form #6 ("both in a Kendra or Trikona together"): the auspicious kendra∪trikona houses.
const AUSPICIOUS_HOUSES = [1, 4, 5, 7, 9, 10];
const NODES = ["rahu", "ketu"];
// §3.1: wealth Yogas involve the 2nd (accumulated), 11th (gains), 5th (merit), 9th (fortune).
const DHANA_HOUSES = [2, 5, 9, 11];
// 07_raja_yogas.md exception list: the NATURAL malefics named for the Kendradhipati rule.
const NATURAL_MALEFIC_LORDS = new Set(["saturn", "mars"]);
// 07_dhana "Benefic in 2nd House": "Natural benefic (Jupiter, Venus, Mercury, unafflicted Moon)"
// — the Moon joins this list only where the doc names it AND it passes moon_unafflicted().
const NATURAL_BENEFICS = ["jupiter", "venus", "mercury"];
// Full natural-malefic list (Sun, Mars, Saturn + the nodes) for occupancy checks like Amal Yoga
// ("EXCLUSIVELY a benefic … No malefics in the 10th") and the benefics-in-kendras Raja Yoga.
const NATURAL_MALEFICS_FULL = ["sun", "mars", "saturn", "rahu", "ketu"];
const DUSTHANA_HOUSES = [6, 8, 12];
// 07_panchamahapurusha_yogas.md: only these five form PMP yogas — "The Sun and Moon do NOT".
const PMP_PLANETS = ["mars", "mercury", "jupiter", "venus", "saturn"];
// The non-luminary classical planets: the count set for lunar flanking yogas ("Any planet
// (other than the Sun)…" — the Moon is the reference point, nodes are classically excluded).
const NON_LUMINARIES = ["mars", "mercury", "jupiter", "venus", "saturn"];
// 07_lunar_solar_yogas.md "Dhan Yoga from Moon": Upachaya houses counted FROM the Moon.
const UPACHAYA_FROM_MOON = [3, 6, 10, 11];

// "Diminished to nil" (07_yoga_fundamentals §1.3) for combust / war-defeated yoga planets.
// 0.15 — the compute_dignity debilitation floor — not a literal 0: the codebase never hard-zeroes
// a house/graha factor (a swallowed planet still exists), and the doc groups combust/war-defeated
// WITH "debilitated" on the same "nil" grading row, so they share the debilitated floor.
const AFFLICTED_FLOOR = 0.15;
// Single-lord Dhana placement forms are graded slightly below the two-lord links: one
// well-placed lord is a weaker wealth promise than two wealth lords actively joined.
const DHANA_SINGLE_LORD_GRADE = 0.85;
// Named-yoga grades, consistent with the scheme above: 1.0 for the doc's full multi-condition
// formations ("Kingdom", "Very affluent", "Highest possible Raja Yoga"), 0.85 for single-planet
// formations, 0.7 for the doc's explicitly PARTIAL/lowest tiers ("Partial yoga", "Moderate
// wealth", the 1-3-exalted tier).
const NAMED_FULL_GRADE = 1.0;
const NAMED_SINGLE_GRADE = 0.85;
const NAMED_PARTIAL_GRADE = 0.7;
// Scorpio-lagna Sun/Moon yogakaraka (02_planetary_relationships.md names them by LORDSHIP
// alone): graded PARTIAL unless the chart additionally links the luminary into the yoga
// structure — see the comment inside raja_forming.
export const SCORPIO_LUMINARY_PARTIAL_GRADE = 0.5;
// 07_yoga_fundamentals grading: "Friendly sign: Moderate results" (the neutral 0.5 tier already
// reads moderate) and 07_dhana Core Wealth exception: lords "debilitated, combust, or in enemy
// signs: Wealth capacity significantly reduced". Enemy-sign placement therefore grades BETWEEN
// neutral (0.5) and the combust/debilitated nil floor (0.15): "significantly reduced", not nil.
const ENEMY_SIGN_FLOOR = 0.3;

// Event categories with an unambiguous Vol_07 yoga (success → Raja, wealth/recognition → Dhana).
const RAJA_CATEGORIES = new Set(["career_breakthrough", "career_contract", "award"]);
// property_purchase joins the Dhana set (2026-07-20): a dated property purchase IS a
// wealth-asset event — §3.1's wealth houses cover it directly (4th: landed property/immovable
// assets; 2nd: accumulated wealth; 11th: gains), so a documented Dhana chart should get yoga
// credit for it exactly as for an award or career windfall.
const DHANA_CATEGORIES = new Set(["award", "career_breakthrough", "career_contract", "property_purchase"]);
// PMP marks the "exceptional person" (status/greatness), so it backs only the status categories.
const PMP_CATEGORIES = new Set(["career_breakthrough", "award"]);

function mod(a: number, b: number): number {
  return ((a % b) + b) % b;
}

/** Smallest angular separation (0–180°) between two longitudes. */
function ang_sep(a: number, b: number): number {
  const d = mod(a - b, 360);
  return d > 180 ? 360 - d : d;
}

function planet_sign(chart: any, planet: string): number | null {
  const pos = chart?.planets?.[planet];
  if (!pos) return null;
  if (pos.sign !== null && pos.sign !== undefined) return mod(Math.trunc(pos.sign), 12);
  return mod(Math.floor(pos.longitude / 30), 12);
}

/** Whole-sign house (1–12) a planet occupies relative to the ascendant sign. */
function house_of(chart: any, asc_sign: number, planet: string): number | null {
  const s = planet_sign(chart, planet);
  return s === null ? null : get_house_from_signs(s, asc_sign);
}

/** Lowercase planet that lords the given house for this ascendant. */
function lord_of(asc_sign: number, house: number): string {
  return SIGN_RULERS[mod(asc_sign + house - 1, 12)].toLowerCase();
}

/** Lowercase planet that lords each of `houses` for this ascendant. */
function lords_of(asc_sign: number, houses: number[]): Set<string> {
  const out = new Set<string>();
  for (const h of houses) out.add(lord_of(asc_sign, h));
  return out;
}

/** A FULL graha aspect (the only drishti that forms a yoga — partials ¼/½/¾ do not). */
function full_aspect(chart: any, from: string, to: string): boolean {
  const sa = planet_sign(chart, from);
  const sb = planet_sign(chart, to);
  if (sa === null || sb === null) return false;
  const distance = mod(sb - sa, 12) + 1; // 1..12, counting inclusive
  return get_drishti_strength(distance, from)[1] >= 1.0;
}

/** §2.1 relationship between two yoga-lord planets: conjunction, mutual full aspect, exchange. */
export function relationship(chart: any, a: string, b: string): string | null {
  const sa = planet_sign(chart, a);
  const sb = planet_sign(chart, b);
  if (sa === null || sb === null || a === b) return null;
  if (sa === sb) return "conjunction";
  if (full_aspect(chart, a, b) && full_aspect(chart, b, a)) return "mutual_aspect";
  // Parivartana: a sits in a sign ruled by b AND b sits in a sign ruled by a.
  if (SIGN_RULERS[sa].toLowerCase() === b && SIGN_RULERS[sb].toLowerCase() === a) return "exchange";
  return null;
}

/** Combustion of `p` by the Sun (classical orbs; the Sun itself never combusts). */
function is_combust_p(chart: any, p: string): boolean {
  if (p === "sun") return false;
  const sun = chart?.planets?.["sun"];
  const pos = chart?.planets?.[p];
  if (!sun || !pos) return false;
  const is_retro = Boolean(pos.is_retrograde ?? false);
  return Boolean(compute_combustion(sun.longitude, pos.longitude, p, is_retro)["is_combust"]);
}

/** Sign-level debilitation of `p` (false when the planet is absent from the chart). */
function is_debilitated_p(chart: any, p: string): boolean {
  const s = planet_sign(chart, p);
  return s !== null && compute_dignity(p, s)[0] === "debilitated";
}

/** Natural relation of `p` toward the RULER of the sign it occupies is "enemy". */
function in_enemy_sign(chart: any, p: string): boolean {
  const s = planet_sign(chart, p);
  return s !== null && natural_relation(p, SIGN_RULERS[s].toLowerCase()) === "enemy";
}

/**
 * Conservative "unafflicted Moon" (07_dhana "Benefic in 2nd House" and 07_raja "Benefics in
 * Kendras" both say "unafflicted Moon"): not combust (compute_combustion, 12° orb), and not
 * sharing a sign with a natural malefic (Mars/Saturn/Rahu/Ketu). War defeat cannot apply — the
 * Moon is not in compute_graha_yuddha's candidate list — so those two are the live checks.
 */
function moon_unafflicted(chart: any): boolean {
  const ms = planet_sign(chart, "moon");
  if (ms === null) return false;
  if (is_combust_p(chart, "moon")) return false;
  return !["mars", "saturn", "rahu", "ketu"].some((m) => planet_sign(chart, m) === ms);
}

/**
 * Raja-yoga-forming planets for a chart (§2.1 links + §2.2 Yoga Karaka), mapped to a formation
 * GRADE (1.0 = full; SCORPIO_LUMINARY_PARTIAL_GRADE for the lordship-only Scorpio luminaries).
 * The Map keeps the Set surface (.has/.size) its callers and tests use.
 */
export function raja_forming(chart: any, asc_sign: number): Map<string, number> {
  // 07_raja_yogas.md §Kendra-Trikona formula: "A lord of a Kendra (1st, 4th, 7th, 10th)
  // establishes any of the following relationships with a lord of a Trikona (5th, 9th — and
  // 1st which is both)" — the 1st lord belongs on BOTH sides of the link forms.
  const kendra = lords_of(asc_sign, KENDRA_HOUSES_FULL);
  const trikona = lords_of(asc_sign, TRIKONA_HOUSES);
  const lord1 = lord_of(asc_sign, 1);
  const forming = new Map<string, number>();
  const add = (p: string, grade = 1.0) => forming.set(p, Math.max(forming.get(p) ?? 0, grade));
  // §Yoga Karaka: "One planet simultaneously owns both a Kendra and a Trikona" — but per the
  // 04_planetary_conjunctions_angles_yoga_karaka.md table the trikona side is the PURE trikonas
  // (5th, 9th) only: "Aries, Gemini, Virgo, Scorpio, Sagittarius: no single clear Yoga Karaka"
  // (an {1,5,9} trikona side wrongly minted Mercury for Gemini/Virgo and Jupiter for
  // Sagittarius/Pisces via their lagna lordship). Mirrors compute_functional_roles in helpers.
  const karaka_kendra = lords_of(asc_sign, KENDRA_HOUSES);
  const karaka_trikona = lords_of(asc_sign, [5, 9]);
  for (const p of karaka_kendra) if (karaka_trikona.has(p)) add(p);
  // Scorpio lagna: 02_planetary_relationships.md "Functional Nature by Ascendant: Scorpio
  // Lagna" explicitly names BOTH luminaries — "Sun and Moon both named as Yogakaraka". The
  // generic derivation can never produce them (each rules a single house), so the source's
  // explicit naming is applied directly (same special case as compute_functional_roles).
  // DISCRIMINATION (2026-07-20): lordship alone holds for EVERY Scorpio-lagna candidate at any
  // birth second, so an unconditional full grade rewarded the lagna without any chart-specific
  // configuration — zero birth-time discrimination beyond the sign itself. The luminaries now
  // form at a PARTIAL grade (0.5) on lordship alone, upgraded to full only when the chart
  // actually LINKS them into the yoga structure: a §2.1-style relationship (conjunction /
  // mutual full aspect / exchange) with another kendra/trikona lord or with each other, or
  // placement in a kendra/trikona house. The doc citation stands — only the GRADE is
  // conditioned, mirroring §1.3's formed-vs-strong distinction.
  if (asc_sign === 7) {
    const scorpio_yoga_lords = new Set([...kendra, ...trikona, "sun", "moon"]);
    for (const lum of ["sun", "moon"]) {
      const h = house_of(chart, asc_sign, lum);
      const linked =
        (h !== null && AUSPICIOUS_HOUSES.includes(h)) ||
        [...scorpio_yoga_lords].some((l) => l !== lum && relationship(chart, lum, l) !== null);
      add(lum, linked ? 1.0 : SCORPIO_LUMINARY_PARTIAL_GRADE);
    }
  }
  // Kendradhipati exception (07_raja_yogas.md: "Natural malefics (Saturn, Mars) owning a Kendra
  // become auspicious ONLY when they simultaneously lord a Trikona — not merely a Kendra"): a
  // pure-Kendra Saturn/Mars cannot act as the kendra-side planet of ANY §2.1 link (forms 1–6).
  // (A Mars/Saturn lagna lord passes — the 1st "is both", per the doc's Aries note "Mars owns
  // 1st (Lagna, auspicious)".)
  const kendra_usable = new Set([...kendra].filter((p) => !NATURAL_MALEFIC_LORDS.has(p) || trikona.has(p)));
  // §2.1 forms 1–3: a Kendra lord and a (different) Trikona lord in conjunction / mutual aspect
  // / exchange. With the 1st lord on the kendra side this now covers the doc's "Maha Raja Yoga"
  // form 1: "Exchange (Parivartana) between the Ascendant lord and the 5th lord".
  for (const k of kendra_usable) for (const t of trikona) if (k !== t && relationship(chart, k, t)) { add(k); add(t); }
  // §2.1 forms 4–5: a Kendra lord placed in a Trikona house, or a Trikona lord placed in a
  // Kendra house. The lagna lord's OWN house is excluded from its placement targets: a link
  // needs a Kendra qualification and a Trikona qualification that are DISTINCT — the 1st lord
  // sitting in the 1st would use the same house for both sides of the "link" (so the AL
  // placement form covers the 5th/9th, and a 5L/9L placed in the 1st forms via form 5).
  for (const k of kendra_usable) {
    const h = house_of(chart, asc_sign, k);
    if (h !== null && TRIKONA_HOUSES.includes(h) && !(k === lord1 && h === 1)) add(k);
  }
  for (const t of trikona) {
    const h = house_of(chart, asc_sign, t);
    if (h !== null && KENDRA_HOUSES_FULL.includes(h) && !(t === lord1 && h === 1)) add(t);
  }
  // 07_raja_yogas.md "9th and 5th Lords Mutual Relationship": "9th lord and 5th lord in mutual
  // conjunction, mutual aspect, or in 7th-from position (opposition). Interpretation: Kingdom."
  // (Opposition IS a mutual 7th-house full aspect, so relationship() covers all three.)
  // Exception: "Both planets must be free from debilitation and combustion."
  const lord9 = lord_of(asc_sign, 9);
  const lord5 = lord_of(asc_sign, 5);
  if (lord9 !== lord5) {
    const rel = relationship(chart, lord9, lord5);
    if (
      (rel === "conjunction" || rel === "mutual_aspect") &&
      ![lord9, lord5].some((p) => is_debilitated_p(chart, p) || is_combust_p(chart, p))
    ) {
      add(lord9);
      add(lord5);
    }
  }
  // §2.1 form 6: a Kendra lord AND a (different) Trikona lord BOTH placed in kendra/trikona houses.
  for (const k of kendra_usable) {
    const hk = house_of(chart, asc_sign, k);
    if (hk === null || !AUSPICIOUS_HOUSES.includes(hk)) continue;
    for (const t of trikona) {
      if (t === k) continue;
      const ht = house_of(chart, asc_sign, t);
      if (ht !== null && AUSPICIOUS_HOUSES.includes(ht)) { add(k); add(t); }
    }
  }
  // "Rahu/Ketu as Yoga Karakas": a node in a Kendra (1/4/7/10) that is conjunct OR receives the
  // full aspect of a Kendra OR Trikona lord becomes a proxy Yoga Karaka (07_raja_yogas.md).
  const yoga_lords = new Set([...kendra, ...trikona]);
  for (const node of NODES) {
    const h = house_of(chart, asc_sign, node);
    if (h === null || !KENDRA_HOUSES_FULL.includes(h)) continue;
    const ns = planet_sign(chart, node);
    for (const lord of yoga_lords) {
      if (planet_sign(chart, lord) === ns || full_aspect(chart, lord, node)) { add(node); break; }
    }
  }
  return forming;
}

// 07_dhana_yogas_wealth_poverty.md "Great Affluence" rows: the 5th house must be a sign RULED
// by `p5`, `p5` must occupy the 5th, and ALL of `in11` must occupy the 11th. (Ruling the 5th
// automatically encodes the doc's sign constraints — e.g. "5th house is a Venus-ruled sign
// (Taurus or Libra)", "5th house is Leo AND Sun is in the 5th", "Cancer as 5th house".)
const GREAT_AFFLUENCE: Array<{ p5: string; in11: string[] }> = [
  { p5: "venus", in11: ["mars"] }, // "Venus in 5th, Mars in 11th → Great riches"
  { p5: "mercury", in11: ["moon", "mars", "jupiter"] }, // "Mercury in 5th, Moon/Mars/Jupiter in 11th"
  { p5: "sun", in11: ["saturn", "moon", "jupiter"] }, // "Sun in 5th (Leo), Saturn/Moon/Jupiter in 11th"
  { p5: "saturn", in11: ["sun", "moon"] }, // "Saturn in 5th (own sign), Sun/Moon in 11th"
  { p5: "jupiter", in11: ["mercury"] }, // "Jupiter in 5th (Jupiter-ruled), Mercury in 11th"
  { p5: "mars", in11: ["venus"] }, // "Mars in 5th (Mars-ruled), Venus in 11th"
  { p5: "moon", in11: ["saturn"] }, // "Moon in 5th (Cancer), Saturn in 11th"
];

/**
 * Dhana-yoga-forming planets → grade, restricted to the forms 07_dhana_yogas_wealth_poverty.md
 * (+ Khadga in 07_special_named_yogas.md, upachaya-from-Moon in 07_lunar_solar_yogas.md)
 * actually documents. The earlier "ANY conjunction/aspect/exchange among the lords of
 * 2/5/9/11" route was an extrapolation no doc states and was DROPPED (2026-07-06) for source
 * fidelity — the documented two-lord routes are the specific exchanges below.
 *   grade 1.0  — the doc's multi-condition formations (exchanges, Great Affluence,
 *                three-benefic upachaya "Very affluent");
 *   grade 0.85 — single-lord placement forms;
 *   grade 0.7  — the doc's explicit "Moderate wealth" tier (two benefics in upachaya).
 * A planet reachable by several routes keeps the strongest grade.
 */
export function dhana_forming(chart: any, asc_sign: number): Map<string, number> {
  const forming = new Map<string, number>();
  const add = (p: string, grade: number) => forming.set(p, Math.max(forming.get(p) ?? 0, grade));
  const lord1 = lord_of(asc_sign, 1);
  const lord2 = lord_of(asc_sign, 2);
  const lord3 = lord_of(asc_sign, 3);
  const lord9 = lord_of(asc_sign, 9);
  const lord11 = lord_of(asc_sign, 11);
  const h2l = house_of(chart, asc_sign, lord2);
  const h11l = house_of(chart, asc_sign, lord11);

  // "Wealth — 2nd and 11th Lord Exchange": "2nd lord is in the 11th house AND 11th lord is in
  // the 2nd house (Parivartana/exchange). Interpretation: Abundant wealth …" (stated twice —
  // also as "2nd Lord in 11th + 11th Lord in 2nd (Wealth Acquired)").
  if (lord2 !== lord11 && h2l === 11 && h11l === 2) { add(lord2, NAMED_FULL_GRADE); add(lord11, NAMED_FULL_GRADE); }
  // "Wealth — 3rd and 11th Lord Exchange": "3rd lord in the 11th AND 11th lord in the 3rd".
  if (lord3 !== lord11 && house_of(chart, asc_sign, lord3) === 11 && h11l === 3) {
    add(lord3, NAMED_FULL_GRADE);
    add(lord11, NAMED_FULL_GRADE);
  }
  // Khadga Yoga (07_special_named_yogas.md): "2nd lord and 9th lord exchange signs
  // (Parivartana) AND Ascendant lord is in a Kendra or Trine."
  const h1l = house_of(chart, asc_sign, lord1);
  if (
    lord2 !== lord9 &&
    relationship(chart, lord2, lord9) === "exchange" &&
    h1l !== null && AUSPICIOUS_HOUSES.includes(h1l)
  ) { add(lord2, NAMED_FULL_GRADE); add(lord9, NAMED_FULL_GRADE); }
  // "Wealth — 11th Lord in 2nd + 2nd Lord in Angle + Jupiter": "11th lord in the 2nd house AND
  // 2nd lord in an angle (Kendra) AND Jupiter associated/supporting." Association is read
  // conservatively as Jupiter occupying the 2nd, being one of the two lords, or conjunct /
  // fully aspecting either lord; per the exception "Jupiter weak or absent reduces this to
  // ordinary wealth", a debilitated/combust Jupiter does not carry the full-grade route.
  if (h11l === 2 && h2l !== null && KENDRA_HOUSES_FULL.includes(h2l)) {
    const js = planet_sign(chart, "jupiter");
    const jup_ok = !is_debilitated_p(chart, "jupiter") && !is_combust_p(chart, "jupiter");
    const jup_assoc =
      lord2 === "jupiter" || lord11 === "jupiter" ||
      house_of(chart, asc_sign, "jupiter") === 2 ||
      (js !== null && [lord2, lord11].some((l) => planet_sign(chart, l) === js || full_aspect(chart, "jupiter", l)));
    if (jup_ok && jup_assoc) { add(lord11, NAMED_FULL_GRADE); add(lord2, NAMED_FULL_GRADE); }
  }
  // "Great Affluence" configurations (see GREAT_AFFLUENCE above): "Great riches"/"Very affluent".
  const sign5 = mod(asc_sign + 4, 12);
  for (const row of GREAT_AFFLUENCE) {
    if (SIGN_RULERS[sign5].toLowerCase() !== row.p5) continue;
    if (house_of(chart, asc_sign, row.p5) !== 5) continue;
    if (!row.in11.every((q) => house_of(chart, asc_sign, q) === 11)) continue;
    add(row.p5, NAMED_FULL_GRADE);
    for (const q of row.in11) add(q, NAMED_FULL_GRADE);
  }
  // "Dhan Yoga from Moon — Benefics in Upachaya from Moon" (07_lunar_solar_yogas.md): "All
  // three benefics in Upachaya from Moon: Very affluent. Two…: Moderate wealth. One…:
  // Negligible wealth" — the one-benefic tier is explicitly negligible, so it does not form.
  const moon_s = planet_sign(chart, "moon");
  if (moon_s !== null) {
    const ups = NATURAL_BENEFICS.filter((b) => {
      const s = planet_sign(chart, b);
      return s !== null && UPACHAYA_FROM_MOON.includes(mod(s - moon_s, 12) + 1);
    });
    if (ups.length === 3) for (const b of ups) add(b, NAMED_FULL_GRADE);
    else if (ups.length === 2) for (const b of ups) add(b, NAMED_PARTIAL_GRADE);
  }
  // "2nd Lord in Kendra or Trikona": promotes wealth acquisition throughout life.
  if (h2l !== null && (KENDRA_HOUSES_FULL.includes(h2l) || [5, 9].includes(h2l))) add(lord2, DHANA_SINGLE_LORD_GRADE);
  // "Wealth — 2nd Lord in Own Sign or Exaltation": "The native accumulates wealth, helps
  // others (philanthropy), and becomes famous". Moolatrikona lies within the own signs.
  const s2 = planet_sign(chart, lord2);
  if (s2 !== null && ["own", "moolatrikona", "exalted"].includes(compute_dignity(lord2, s2)[0]))
    add(lord2, DHANA_SINGLE_LORD_GRADE);
  // "Wealth — Jupiter as 2nd Lord or Jupiter with Mars in 2nd": "Jupiter is the 2nd lord, OR
  // Jupiter is placed with Mars in the 2nd house. Interpretation: Wealth confirmed."
  if (lord2 === "jupiter") add("jupiter", DHANA_SINGLE_LORD_GRADE);
  if (house_of(chart, asc_sign, "jupiter") === 2 && house_of(chart, asc_sign, "mars") === 2) {
    add("jupiter", DHANA_SINGLE_LORD_GRADE);
    add("mars", DHANA_SINGLE_LORD_GRADE);
  }
  // "11th Lord in Kendra (Even if Combust)": many gains (combustion-exempt — see strength_factor).
  if (h11l !== null && KENDRA_HOUSES_FULL.includes(h11l)) add(lord11, DHANA_SINGLE_LORD_GRADE);
  // "Benefic in 2nd House": "Natural benefic (Jupiter, Venus, Mercury, unafflicted Moon) placed
  // in the 2nd house" — the Moon counts when unafflicted (see moon_unafflicted).
  for (const b of NATURAL_BENEFICS) if (house_of(chart, asc_sign, b) === 2) add(b, DHANA_SINGLE_LORD_GRADE);
  if (house_of(chart, asc_sign, "moon") === 2 && moon_unafflicted(chart)) add("moon", DHANA_SINGLE_LORD_GRADE);
  // "Wealth — 9th Lord Strong (Abundant Fortune)": strong "by exaltation, own sign,
  // Mooltrikona, OR angular/trinal placement" — placement now qualifies too (widened
  // 2026-07-06; the ≥0.85-dignity-only reading was too narrow). Exception honoured as a gate:
  // "9th lord debilitated, combust, or in a dusthana: fortune is delayed or diminished."
  // The 5th lord appears only in the PAIRED "Core Wealth Formula — 9th AND 5th Lords", so a
  // strong 5th lord still counts only alongside the strong 9th lord.
  const strong = (lord: string): boolean => {
    const s = planet_sign(chart, lord);
    if (s === null) return false;
    const [label, value] = compute_dignity(lord, s);
    const h = get_house_from_signs(s, asc_sign);
    if (label === "debilitated" || DUSTHANA_HOUSES.includes(h)) return false;
    return value >= 0.85 || AUSPICIOUS_HOUSES.includes(h);
  };
  if (strong(lord9)) {
    add(lord9, DHANA_SINGLE_LORD_GRADE);
    const lord5 = lord_of(asc_sign, 5);
    if (strong(lord5)) add(lord5, DHANA_SINGLE_LORD_GRADE);
  }
  return forming;
}

/**
 * Panchamahapurusha formation (07_panchamahapurusha_yogas.md): Mars/Mercury/Jupiter/Venus/Saturn
 * in its OWN or EXALTATION sign AND that sign in a Kendra (1/4/7/10) from the Lagna. Debilitation
 * "does NOT form" the yoga — guaranteed here because own/exalt excludes the debilitation sign.
 */
export function pmp_forming(chart: any, asc_sign: number): Set<string> {
  const forming = new Set<string>();
  for (const p of PMP_PLANETS) {
    const s = planet_sign(chart, p);
    if (s === null) continue;
    // Doc: "own sign OR exaltation". "moolatrikona" is accepted too: every PMP planet's MT
    // zone lies WITHIN one of its own signs (Mars/Aries, Mercury/Virgo, Jupiter/Sagittarius,
    // Venus/Libra, Saturn/Aquarius), so an MT label still means "in its own sign" — the
    // sign-level formation condition holds (compute_dignity now distinguishes the MT subzone).
    const [label] = compute_dignity(p, s);
    if (label !== "own" && label !== "exalted" && label !== "moolatrikona") continue;
    const h = mod(s - asc_sign, 12) + 1;
    if (KENDRA_HOUSES_FULL.includes(h)) forming.add(p);
  }
  return forming;
}

/**
 * 07_panchamahapurusha_yogas.md formation-rule exception: "Even if the planet is strong for
 * its Panchamahapurusha Yoga, if Sun AND Moon are both weak, the native will NOT attain
 * kinghood." WEAK is defined here as: Sun debilitated (Libra); Moon debilitated (Scorpio) OR
 * combust (dark, within the 12° combustion orb of the Sun). BOTH luminaries must be present
 * in the chart AND weak — a stub chart missing a luminary cannot testify to its weakness.
 * When true, PMP formation is still computed and audited but reads grade-neutral (score 50).
 */
export function pmp_luminaries_weak(chart: any): boolean {
  const sun_s = planet_sign(chart, "sun");
  const moon_s = planet_sign(chart, "moon");
  if (sun_s === null || moon_s === null) return false;
  const sun_weak = compute_dignity("sun", sun_s)[0] === "debilitated";
  const moon_weak = compute_dignity("moon", moon_s)[0] === "debilitated" || is_combust_p(chart, "moon");
  return sun_weak && moon_weak;
}

/**
 * Named status/Raja yogas the sources specify beyond the §2.1 link forms — each entry cites
 * its doc. Returns forming planets → grade plus the formed yoga names for the audit trail.
 * These are Raja/status yogas, so chart-wide Yoga Bhanga applies to them exactly as to the
 * §2.1 route, and they require dasha activation like any other yoga (their Timing sections
 * all say "Dasha of …").
 */
export function named_status_forming(
  chart: any,
  asc_sign: number,
): { forming: Map<string, number>; names: string[] } {
  const forming = new Map<string, number>();
  const names: string[] = [];
  const add = (p: string, grade: number) => forming.set(p, Math.max(forming.get(p) ?? 0, grade));
  const moon_s = planet_sign(chart, "moon");

  // ── Gaja Kesari (07_lunar_solar_yogas.md): "Jupiter in a Kendra house (1st, 4th, 7th, or
  // 10th) from the Ascendant OR from the Moon, AND Jupiter is not debilitated (not in
  // Capricorn), not combust, not in an enemy sign, AND receives the aspect or conjunction of
  // a benefic." Exception: "Jupiter in Kendra from Moon but not from Ascendant: Partial yoga".
  const js = planet_sign(chart, "jupiter");
  if (
    js !== null &&
    !is_debilitated_p(chart, "jupiter") &&
    !is_combust_p(chart, "jupiter") &&
    !in_enemy_sign(chart, "jupiter")
  ) {
    const from_asc = KENDRA_HOUSES_FULL.includes(mod(js - asc_sign, 12) + 1);
    const from_moon = moon_s !== null && KENDRA_HOUSES_FULL.includes(mod(js - moon_s, 12) + 1);
    const blessed = ["venus", "mercury", "moon"].some(
      (b) => planet_sign(chart, b) === js || full_aspect(chart, b, "jupiter"),
    );
    if ((from_asc || from_moon) && blessed) {
      add("jupiter", from_asc ? NAMED_SINGLE_GRADE : NAMED_PARTIAL_GRADE);
      names.push(from_asc ? "gaja_kesari" : "gaja_kesari_partial");
    }
  }

  // ── Adhi Yoga (07_raja_yogas.md): "All three natural benefics — Jupiter, Mercury, and Venus
  // — occupy the 6th, 7th, and 8th houses from the Moon … Sun must NOT be among them. No
  // malefic aspects on these benefics." Tiers: "All three … king; Two … minister; One … Army
  // chief." Exception: "Malefic aspect on any of the three benefics destroys the yoga."
  if (moon_s !== null) {
    const zone = [mod(moon_s + 5, 12), mod(moon_s + 6, 12), mod(moon_s + 7, 12)];
    const sun_s = planet_sign(chart, "sun");
    if (sun_s === null || !zone.includes(sun_s)) {
      const participants = NATURAL_BENEFICS.filter((b) => {
        const s = planet_sign(chart, b);
        return s !== null && zone.includes(s);
      });
      const afflicted = participants.some((b) => {
        const bs = planet_sign(chart, b);
        return (
          ["mars", "saturn", "rahu", "ketu"].some((m) => planet_sign(chart, m) === bs) ||
          ["sun", "mars", "saturn"].some((m) => full_aspect(chart, m, b))
        );
      });
      if (participants.length > 0 && !afflicted) {
        const grade =
          participants.length === 3 ? NAMED_FULL_GRADE : participants.length === 2 ? NAMED_SINGLE_GRADE : NAMED_PARTIAL_GRADE;
        for (const b of participants) add(b, grade);
        names.push("adhi");
      }
    }
  }

  // ── Sunapha / Anapha / Durudura (07_lunar_solar_yogas.md): "Any planet (other than the Sun)
  // placed in the 2nd house from the Moon" (Sunapha, "King or equal to a king…"), "…in the
  // 12th house from the Moon" (Anapha), "…in BOTH the 2nd AND 12th" (Durudura). The nodes are
  // classically excluded; gates per the docs: Sunapha "The planet must have reasonable
  // strength", Anapha "Planet must not be debilitated or combust" (applied to both flanks).
  if (moon_s !== null) {
    const flank = (sign: number) =>
      NON_LUMINARIES.filter(
        (p) => planet_sign(chart, p) === sign && !is_debilitated_p(chart, p) && !is_combust_p(chart, p),
      );
    const in2nd = flank(mod(moon_s + 1, 12));
    const in12th = flank(mod(moon_s - 1, 12));
    if (in2nd.length > 0 && in12th.length > 0) {
      for (const p of [...in2nd, ...in12th]) add(p, NAMED_SINGLE_GRADE);
      names.push("durudhara");
    } else if (in2nd.length > 0) {
      for (const p of in2nd) add(p, NAMED_PARTIAL_GRADE);
      names.push("sunapha");
    } else if (in12th.length > 0) {
      for (const p of in12th) add(p, NAMED_PARTIAL_GRADE);
      names.push("anapha");
    }
  }

  // ── Amal Yoga (07_lunar_solar_yogas.md): "EXCLUSIVELY a benefic planet (Jupiter, Venus,
  // Mercury, or strong Moon) placed in the 10th house from the Ascendant OR from the Moon. No
  // malefics in the 10th." "Any malefic in the 10th disqualifies this yoga." A strong Moon =
  // not debilitated and not combust.
  const amal_benefics = (ref_sign: number): string[] => {
    const tenth = mod(ref_sign + 9, 12);
    const occupants = [...PLANETS, ...NODES].filter((p) => planet_sign(chart, p) === tenth);
    if (occupants.length === 0) return [];
    const qualifies = (p: string) =>
      NATURAL_BENEFICS.includes(p) ||
      (p === "moon" && !is_debilitated_p(chart, "moon") && !is_combust_p(chart, "moon"));
    return occupants.every(qualifies) ? occupants : [];
  };
  const amal = new Set<string>([...amal_benefics(asc_sign), ...(moon_s !== null ? amal_benefics(moon_s) : [])]);
  if (amal.size > 0) {
    for (const p of amal) add(p, NAMED_SINGLE_GRADE);
    names.push("amala");
  }

  // ── Lakshmi Yoga (07_special_named_yogas.md): "9th lord placed in a Kendra house in its
  // Mooltrikona, own sign, or exaltation sign AND Ascendant lord is strong." ("The
  // Mooltrikona/own/exaltation requirement for 9th lord is mandatory.")
  const lord9 = lord_of(asc_sign, 9);
  const lord1 = lord_of(asc_sign, 1);
  const s9 = planet_sign(chart, lord9);
  const s1 = planet_sign(chart, lord1);
  if (s9 !== null && s1 !== null) {
    const h9 = get_house_from_signs(s9, asc_sign);
    const dignified = ["moolatrikona", "own", "exalted"].includes(compute_dignity(lord9, s9)[0]);
    const al_strong = compute_dignity(lord1, s1)[1] >= 0.85; // own/MT/exalted
    if (KENDRA_HOUSES_FULL.includes(h9) && dignified && al_strong) {
      add(lord9, NAMED_FULL_GRADE);
      add(lord1, NAMED_FULL_GRADE);
      names.push("lakshmi");
    }
  }

  // ── Raja Yoga — Multiple Exalted Planets (07_raja_yogas.md): "1, 2, or 3 planets in
  // exaltation: A royal scion becomes a king; another becomes equal to a king, or wealthy.
  // 4 or 5 planets in exaltation (exaltation or Mooltrikona signs): Even a person of base
  // birth becomes a king. 6 planets in exaltation: Emperor." Timing: "Dashas of the exalted
  // planets" (combust/war-lost exalted planets are nil'd by strength_factor per the doc's
  // exception).
  const label_of = (p: string): string => {
    const s = planet_sign(chart, p);
    return s === null ? "" : compute_dignity(p, s)[0];
  };
  const exalted = PLANETS.filter((p) => label_of(p) === "exalted");
  const exalted_or_mt = PLANETS.filter((p) => ["exalted", "moolatrikona"].includes(label_of(p)));
  if (exalted_or_mt.length >= 4) {
    for (const p of exalted_or_mt) add(p, NAMED_FULL_GRADE);
    names.push("multiple_exalted_4plus");
  } else if (exalted.length >= 1) {
    for (const p of exalted) add(p, NAMED_PARTIAL_GRADE);
    names.push("exalted_planets_tier");
  }

  // ── Raja Yoga — Benefics in Kendras, Malefics in 3rd/6th/11th (07_raja_yogas.md): "All
  // natural benefics (Jupiter, Venus, Mercury, unafflicted Moon) in angular houses (1st, 4th,
  // 7th, 10th) AND malefics simultaneously in 3rd, 6th, and 11th houses." Exceptions: "Any
  // malefic in an angle significantly reduces this yoga. Ascendant lord must also be
  // reasonably strong" (read as: not debilitated, not combust).
  {
    const bens_ok = NATURAL_BENEFICS.every((b) => {
      const h = house_of(chart, asc_sign, b);
      return h !== null && KENDRA_HOUSES_FULL.includes(h);
    });
    const moon_h = house_of(chart, asc_sign, "moon");
    const moon_ok = !moon_unafflicted(chart) || (moon_h !== null && KENDRA_HOUSES_FULL.includes(moon_h));
    const mals_ok = NATURAL_MALEFICS_FULL.every((m) => {
      const h = house_of(chart, asc_sign, m);
      return h === null || [3, 6, 11].includes(h);
    });
    const al_ok = s1 !== null && !is_debilitated_p(chart, lord1) && !is_combust_p(chart, lord1);
    if (bens_ok && moon_ok && mals_ok && al_ok) {
      for (const b of NATURAL_BENEFICS) add(b, NAMED_FULL_GRADE);
      if (moon_unafflicted(chart) && moon_h !== null && KENDRA_HOUSES_FULL.includes(moon_h)) add("moon", NAMED_FULL_GRADE);
      names.push("benefics_angles_malefics_upachaya");
    }
  }

  // ── Raja Yoga — Karm (10th) Lord Exalted in Kendra (07_raja_yogas.md): "10th lord in
  // exaltation AND that exaltation sign is also an angular house (Kendra), aspecting the
  // Ascendant." Exceptions: "The exaltation must be in a Kendra, not a dusthana. Aspect on
  // Ascendant is required" (full graha drishti onto the lagna sign).
  const lord10 = lord_of(asc_sign, 10);
  const s10 = planet_sign(chart, lord10);
  if (s10 !== null && compute_dignity(lord10, s10)[0] === "exalted") {
    const h10 = get_house_from_signs(s10, asc_sign);
    const aspects_lagna = get_drishti_strength(mod(asc_sign - s10, 12) + 1, lord10)[1] >= 1.0;
    // OCCUPANCY of the lagna satisfies the "aspecting the Ascendant" requirement too
    // (2026-07-20): no graha casts a drishti on its own sign (distance 1 has no aspect), so an
    // exalted 10th lord STANDING IN the 1st — a strictly stronger contact with the Ascendant
    // than any aspect — used to fail the drishti-only reading. Occupancy is accepted alongside
    // the full drishti (the 1st is in the doc's own Kendra list).
    if (KENDRA_HOUSES_FULL.includes(h10) && (aspects_lagna || h10 === 1)) {
      add(lord10, NAMED_FULL_GRADE);
      names.push("karmadhipati_exalted_kendra");
    }
  }

  return { forming, names };
}

// ── Nabhasha yogas (07_nabhasha_yogas.md) ────────────────────────────────────────────────────
// "Results of Nabhasha Yogas are felt throughout ALL Dasha periods — they are permanently
// active, not only during specific Dashas" — the ONE yoga family that BYPASSES the dasha
// activation gate (07_yoga_fundamentals: "Exceptions: Nabhasha Yogas … are permanently active
// across ALL Dasha periods"). Only the doc's clean, status/wealth-relevant patterns are scored:
//   Kamal   ("attains kingly status")          → status
//   Chakra  ("Emperor; kings prostrate")       → status
//   Vapi    ("lasting wealth, kingly status")  → wealth + status
//   Samudra ("abundant wealth, firm wealth")   → wealth
//   Daam    ("very affluent" — Sankhya tier, "overridden if another Nabhasha Yoga applies",
//            so it is gated on the ABSENCE of every non-Sankhya Nabhasha pattern) → wealth
// Patterns use the seven classical planets (nodes are not part of the classical Nabhasha
// distribution counts); a chart missing any of the seven cannot testify to a distribution.

const ODD_HOUSES = [1, 3, 5, 7, 9, 11];
const EVEN_HOUSES = [2, 4, 6, 8, 10, 12];
const PANAPHARA_HOUSES = [2, 5, 8, 11];
const APOKLIMA_HOUSES = [3, 6, 9, 12];

/** True when ANY non-Sankhya Nabhasha pattern (Asraya / Dala / Akriti) is present — the
 *  doc's override gate for the Sankhya tier ("Sankhya Yogas are overridden if another
 *  Nabhasha Yoga applies"). Checks are set-membership over the 7 classical planets. */
function non_sankhya_nabhasha_present(signs: Record<string, number>, houses: Record<string, number>): boolean {
  const H = [...new Set(Object.values(houses))];
  const subset = (allowed: number[]) => H.every((h) => allowed.includes(h));
  // Asraya (Rajju/Musala/Nala): all planets in only Movable / only Fixed / only Dual signs.
  const svals = Object.values(signs);
  for (const m of [0, 1, 2]) if (svals.every((s) => mod(s, 3) === m)) return true;
  // Akriti 7-successive-house family (Nauka/Koot/Chatra/Chap + Ardha Chandra): all planets
  // within SOME window of 7 successive houses (this also covers Gada/Sakata/Vihaga/Yup/Shar/
  // Shakti/Danda, whose house sets all fit inside a 7-window).
  for (let start = 1; start <= 12; start++) {
    const window = Array.from({ length: 7 }, (_, i) => mod(start - 1 + i, 12) + 1);
    if (subset(window)) return true;
  }
  // Chakra / Samudra (alternate houses), Kamal / Vapi confinements, Shringataka (trines),
  // Hal's {2,6,10}/{4,8,12} are subsets of the even houses and {3,7,11} of the odd.
  if (subset(ODD_HOUSES) || subset(EVEN_HOUSES)) return true;
  if (subset(KENDRA_HOUSES_FULL) || subset(PANAPHARA_HOUSES) || subset(APOKLIMA_HOUSES)) return true;
  if (subset(TRIKONA_HOUSES)) return true; // Shringataka: "All planets in 1st, 5th, and 9th"
  // Dala — Mala/Sarpa: "Three or more Kendra houses occupied by natural benefics [/malefics]"
  // (an occupant of the opposite camp in a kendra breaks it).
  const kendra_occupants = PLANETS.filter((p) => KENDRA_HOUSES_FULL.includes(houses[p]));
  const kendras_used = new Set(kendra_occupants.map((p) => houses[p]));
  if (kendras_used.size >= 3) {
    if (kendra_occupants.every((p) => ["jupiter", "venus", "mercury", "moon"].includes(p))) return true; // Mala
    if (kendra_occupants.every((p) => ["sun", "mars", "saturn"].includes(p))) return true; // Sarpa
  }
  // Akriti Vajra/Yava: "All benefics in 1st and 7th (or all malefics in 4th and 10th)" / the
  // reverse. Benefics here = Jupiter/Venus/Mercury/Moon; malefics = Sun/Mars/Saturn.
  const ben_h = ["jupiter", "venus", "mercury", "moon"].map((p) => houses[p]);
  const mal_h = ["sun", "mars", "saturn"].map((p) => houses[p]);
  const within = (hs: number[], allowed: number[]) => hs.every((h) => allowed.includes(h));
  if (within(ben_h, [1, 7]) || within(mal_h, [4, 10])) return true; // Vajra
  if (within(ben_h, [4, 10]) || within(mal_h, [1, 7])) return true; // Yava
  return false;
}

/**
 * Nabhasha formation → route grades for the status and wealth event families, plus the formed
 * pattern names for the audit trail. Returns zero grades when any classical planet is missing.
 */
export function nabhasha_forming(
  chart: any,
  asc_sign: number,
): { status: number; wealth: number; names: string[] } {
  const none = { status: 0, wealth: 0, names: [] as string[] };
  const signs: Record<string, number> = {};
  const houses: Record<string, number> = {};
  for (const p of PLANETS) {
    const s = planet_sign(chart, p);
    if (s === null) return none;
    signs[p] = s;
    houses[p] = get_house_from_signs(s, asc_sign);
  }
  const house_set = new Set(Object.values(houses));
  const H = [...house_set];
  const subset = (allowed: number[]) => H.every((h) => allowed.includes(h));
  let status = 0;
  let wealth = 0;
  const names: string[] = [];
  // Kamal: "All planets distributed across all four Kendra houses (1st, 4th, 7th, 10th) …
  // attains kingly status" — all four kendras must be used ("across all four").
  if (subset(KENDRA_HOUSES_FULL) && house_set.size === 4) { status = Math.max(status, NAMED_FULL_GRADE); names.push("kamal"); }
  // Chakra: "All planets in alternate signs starting from the 1st house (odd signs: 1st, 3rd,
  // 5th, 7th, 9th, 11th) … Emperor; kings prostrate at his feet."
  if (subset(ODD_HOUSES)) { status = Math.max(status, NAMED_FULL_GRADE); names.push("chakra"); }
  // Vapi: "All planets in all four Panapharas (2nd/5th/8th/11th) OR all four Apoklimas
  // (3rd/6th/9th/12th) … lasting wealth … attains kingly status" ("all four" ⇒ four houses used).
  if ((subset(PANAPHARA_HOUSES) || subset(APOKLIMA_HOUSES)) && house_set.size === 4) {
    status = Math.max(status, NAMED_FULL_GRADE);
    wealth = Math.max(wealth, NAMED_FULL_GRADE);
    names.push("vapi");
  }
  // Samudra: "All planets in alternate signs starting from the 2nd house (even signs …) …
  // abundant wealth, dear to people, firm wealth."
  if (subset(EVEN_HOUSES)) { wealth = Math.max(wealth, NAMED_FULL_GRADE); names.push("samudra"); }
  // Daam (Sankhya): "All planets in 6 signs only … righteously earned wealth, very affluent"
  // — graded a notch below the Akriti forms because the doc subordinates the whole Sankhya
  // tier ("Overridden if another Nabhasha Yoga applies" — hence the override gate).
  const sign_count = new Set(Object.values(signs)).size;
  if (sign_count === 6 && !non_sankhya_nabhasha_present(signs, houses)) {
    wealth = Math.max(wealth, NAMED_SINGLE_GRADE);
    names.push("daam");
  }
  return { status, wealth, names };
}

/**
 * Yoga Bhanga — chart-wide cancellation (07_yoga_fundamentals_and_cancellation.md, sections
 * "Yoga Bhanga — General Cancellation Conditions" and "Raja Yoga Obstructions — Extended List").
 * Only the rules computable from the natal chart ALONE are implemented; returns the doc-derived
 * reason string, or null when no cancellation applies.
 *
 * Not implemented: the Moon-navamsa-sandhi rule (degree-edge noise) and the compensation-quality
 * folded rules. The "five planets in inimical signs", "Venus in debilitation Navamsa",
 * debilitated-malefics + Moon-in-Scorpio, Jupiter-in-Capricorn-lagna and Vargottama-lagna
 * rules ARE implemented below (2026-07-06 — the earlier "no friendship model exists" premise
 * was false: helpers.natural_relation carries the classical friendship table).
 */
export function yoga_bhanga(chart: any, asc_sign: number): string | null {
  const planets = chart?.planets ?? {};

  // "Sun at 10° Libra (deep debilitation) — destroys ALL Raja Yogas in the chart." Strict
  // reading: within 1° of the deep-debilitation point (exaltation point + 180° = 190°).
  const sun = planets["sun"];
  const sun_deep = (deep_exaltation_longitude("sun") ?? 10.0) + 180.0;
  if (sun && ang_sep(sun.longitude, sun_deep) <= 1.0) return "sun_deep_debilitation_10_libra";

  // Extended List: "Two or more of Mars/Saturn/Jupiter/Sun in debilitation + one in Ascendant
  // + Moon in Scorpio → Raja Yogas futile." Checked BEFORE the generic ≥3-debilitated count
  // (a Scorpio Moon is itself debilitated, so this narrower rule would otherwise be shadowed
  // and its more specific audit reason lost).
  const debil_set = ["mars", "saturn", "jupiter", "sun"].filter((p) => is_debilitated_p(chart, p));
  if (
    debil_set.length >= 2 &&
    debil_set.some((p) => house_of(chart, asc_sign, p) === 1) &&
    planet_sign(chart, "moon") === 7
  ) return "debilitated_malefics_lagna_moon_scorpio";

  // "Three or more planets in debilitation simultaneously" cancel even strong yogas ("even
  // emperor's son becomes a beggar"). Counted over the 7 classical planets — the nodes' fall
  // signs are classically disputed, so they are left out of the count.
  let fallen = 0;
  for (const p of PLANETS) {
    const s = planet_sign(chart, p);
    if (s !== null && compute_dignity(p, s)[0] === "debilitated") fallen += 1;
  }
  if (fallen >= 3) return "three_or_more_debilitated";

  // "Moon in deep debilitation (3° Scorpio) with no beneficial aspects." Same 1° strictness;
  // "beneficial aspects" = a natural benefic conjunct the Moon or casting a FULL drishti on it.
  const moon = planets["moon"];
  // deep_exaltation_longitude returns an ABSOLUTE longitude (Moon → 33.0 = 3° Taurus), so the
  // defensive fallback must be 33.0 too, not the 3.0 degree-in-sign (caught 2026-07-02: a 3.0
  // fallback would have tested 3° Libra instead of 3° Scorpio if the table entry ever vanished).
  const moon_deep = (deep_exaltation_longitude("moon") ?? 33.0) + 180.0;
  if (moon && ang_sep(moon.longitude, moon_deep) <= 1.0) {
    const ms = planet_sign(chart, "moon");
    const helped = NATURAL_BENEFICS.some((b) => planet_sign(chart, b) === ms || full_aspect(chart, b, "moon"));
    if (!helped) return "moon_deep_debilitation_unaspected";
  }

  // "Benefics in dusthanas (6th, 8th, 12th) while malefics occupy angles." Conservative reading
  // (cancellation must be rare, this component never penalises lightly): ALL three natural
  // benefics in dusthanas AND BOTH natural malefics (Mars, Saturn) in kendras.
  const benefics_fallen = NATURAL_BENEFICS.every((b) => {
    const h = house_of(chart, asc_sign, b);
    return h !== null && DUSTHANA_HOUSES.includes(h);
  });
  const malefics_angular = ["mars", "saturn"].every((m) => {
    const h = house_of(chart, asc_sign, m);
    return h !== null && KENDRA_HOUSES_FULL.includes(h);
  });
  if (benefics_fallen && malefics_angular) return "benefics_in_dusthanas_malefics_in_angles";

  // "Moon in Kemadruma … without compensating aspects." The FULL formation per
  // 07_lunar_solar_yogas.md: "No planet (excluding the Sun) in the 1st, 2nd, or 12th from the
  // Moon AND no planet in any Kendra from the Ascendant" — BOTH conjuncts are required (the
  // second was missing before 2026-07-06, over-triggering this bhanga). The Moon-side count
  // classically excludes the Sun and the nodes; the kendra-from-Ascendant count takes any
  // classical planet (a Moon or Sun in an angle voids the dosha too). A planet CONJUNCT the
  // Moon or any FULL drishti on the Moon is the compensation that voids the dosha.
  const ms = planet_sign(chart, "moon");
  if (ms !== null) {
    const flanked = NON_LUMINARIES.some((p) => {
      const s = planet_sign(chart, p);
      return s !== null && (s === ms || s === mod(ms + 1, 12) || s === mod(ms - 1, 12));
    });
    const kendra_occupied = PLANETS.some((p) => {
      const h = house_of(chart, asc_sign, p);
      return h !== null && KENDRA_HOUSES_FULL.includes(h);
    });
    const aspected = PLANETS.some((p) => p !== "moon" && full_aspect(chart, p, "moon"));
    if (!flanked && !kendra_occupied && !aspected) return "kemadruma_moon";
  }

  // "Five planets in inimical signs or combustion" (§General) — taken at the Extended List's
  // STRICTER conditional reading: "Five planets in inimical signs/combustion + Sun or Moon not
  // in exaltation → kingly Yogas ineffective." Inimical = natural_relation of the planet toward
  // its sign's ruler is "enemy" (02_planetary_relationships.md natural friendship table).
  const sun_exalted = (() => { const s = planet_sign(chart, "sun"); return s !== null && compute_dignity("sun", s)[0] === "exalted"; })();
  const moon_exalted = (() => { const s = planet_sign(chart, "moon"); return s !== null && compute_dignity("moon", s)[0] === "exalted"; })();
  if (!sun_exalted && !moon_exalted) {
    let afflicted = 0;
    for (const p of PLANETS) if (in_enemy_sign(chart, p) || is_combust_p(chart, p)) afflicted += 1;
    if (afflicted >= 5) return "five_planets_inimical_or_combust";
  }

  // Extended List: "Jupiter in Capricorn (debilitation) as Ascendant with Moon not in Cancer
  // → miserable" — Jupiter debilitated IN the Capricorn lagna, unless the Moon sits in Cancer.
  if (
    asc_sign === 9 &&
    planet_sign(chart, "jupiter") === 9 &&
    planet_sign(chart, "moon") !== null &&
    planet_sign(chart, "moon") !== 3
  ) return "jupiter_debilitated_capricorn_lagna";

  // Extended List: "Ascendant in Vargottama position without any planetary aspect → Raja Yogas
  // fail." Aspect = a classical planet occupying the lagna sign or casting a FULL graha drishti
  // onto it (occupancy counted as association keeps this cancellation conservative/rare).
  if (typeof chart?.ascendant_longitude === "number" && is_vargottama(chart.ascendant_longitude)) {
    const attended = PLANETS.some((p) => {
      const s = planet_sign(chart, p);
      return s !== null && (s === asc_sign || get_drishti_strength(mod(asc_sign - s, 12) + 1, p)[1] >= 1.0);
    });
    if (!attended) return "vargottama_lagna_unaspected";
  }

  // Extended List: "Venus in debilitation Navamsa → even vast kingdom is taken from the
  // native." Venus's D9 sign (get_divisional_position) in Virgo — Venus's debilitation sign.
  const venus = planets["venus"];
  if (venus && get_divisional_position(venus.longitude, 9)[0] === 5) return "venus_debilitated_navamsa";

  // Subsumed by the rules above: "Four or more planets in fall" and "Aquarius Ascendant with
  // three planets debilitated" (both are ≥3 debilitated planets).
  return null;
}

/**
 * §1.3 strength grade of the yoga-forming planets, lifted if confirmed in Navamsa. Per
 * 07_yoga_fundamentals ("free from debilitation, combustion, or planetary war"; "Combust,
 * debilitated, or defeated in planetary war: Diminished to nil"), a combust or war-DEFEATED
 * yoga planet is dropped to the debilitated floor (0.15 — see AFFLICTED_FLOOR for why not a
 * literal 0). War victor per 05_shadbala_kaal_bala.md: "planet with the lower degree within its
 * sign is the victor" — only the LOSER is nil'd. `combust_exempt` carries the 07_dhana "11th
 * Lord in Kendra (Even if Combust)" explicit exemption.
 *
 * AGGREGATION (2026-07-20). Default "max": the factor is the STRONGEST per-planet
 * (dignity × route-grade) contribution. The earlier Σ(value×grade)/n mean meant that forming
 * an EXTRA, weaker yoga LOWERED the confirmation — a chart with two strong yogas plus one weak
 * partial scored BELOW the same chart with just the two strong ones, punishing formation.
 * Under max, an additional formed yoga can never reduce the factor (monotone in formation),
 * and the strongest documented promise sets the grade — matching §1.3, which grades each yoga
 * by ITS planets' dignity, not by averaging unrelated yogas together. "mean" is kept for the
 * Nabhasha route ONLY: there the forming map is one 7-planet DISTRIBUTION pattern (a single
 * yoga whose membership never grows), so the mean planetary dignity is the honest grade of
 * that one yoga and no monotonicity concern exists.
 *
 * Exported for the monotonicity pin in yoga_scoring.test.ts.
 */
export function strength_factor(
  chart: any,
  forming: Map<string, number>,
  combust_exempt: Set<string>,
  aggregation: "max" | "mean" = "max",
): { factor: number; navamsa_confirmed: boolean; combust: string[]; war_defeated: string[] } {
  if (!forming.size) return { factor: 0, navamsa_confirmed: false, combust: [], war_defeated: [] };

  const defeated = _chart_yoga_state(chart).war_defeated;
  const sun = chart?.planets?.["sun"];

  let sum = 0;
  let best = 0;
  let navamsa_confirmed = false;
  const combust_list: string[] = [];
  const war_list: string[] = [];
  for (const [p, grade] of forming) {
    const s = planet_sign(chart, p);
    let value = s === null ? 0.5 : compute_dignity(p, s)[1]; // 0.15 (debilitated) .. 1.0 (exalted)
    // Enemy-sign reduction (07_dhana Core Wealth exception: lords "debilitated, combust, or in
    // enemy signs: Wealth capacity significantly reduced"; 07_yoga_fundamentals grades only
    // down to "Friendly sign: Moderate results" — enemy placement sits below that): a yoga
    // planet in a natural enemy's sign drops to ENEMY_SIGN_FLOOR (0.3), between neutral and
    // the combust/debilitated nil floor. Own/MT/exalted placements are self-ruled or
    // friendly-ruled for every classical planet, so this only ever bites the neutral tier.
    if (s !== null && natural_relation(p, SIGN_RULERS[s].toLowerCase()) === "enemy") {
      value = Math.min(value, ENEMY_SIGN_FLOOR);
    }
    const pos = chart?.planets?.[p];
    let is_combust = false;
    if (p !== "sun" && sun && pos) {
      const is_retro = Boolean(pos.is_retrograde ?? false);
      is_combust =
        Boolean(compute_combustion(sun.longitude, pos.longitude, p, is_retro)["is_combust"]) &&
        !combust_exempt.has(p);
    }
    const war_lost = defeated.has(p);
    if (is_combust) combust_list.push(p);
    if (war_lost) war_list.push(p);
    if (is_combust || war_lost) value = Math.min(value, AFFLICTED_FLOOR); // "diminished to nil"
    sum += value * grade;
    best = Math.max(best, value * grade);
    // §1.3 Navamsa confirmation — only an UNAFFLICTED yoga planet can certify the yoga in D9
    // (a planet nil'd in the Rasi cannot deliver "results are certain").
    if (pos && !is_combust && !war_lost) {
      const nav_sign = get_divisional_position(pos.longitude, 9)[0];
      if (compute_dignity(p, nav_sign)[1] >= 0.85) navamsa_confirmed = true; // own/MT/exalted in D9
    }
  }
  // "max" (default) is monotone in formation; "mean" grades the single Nabhasha distribution.
  let factor = aggregation === "max" ? best : sum / forming.size;
  if (navamsa_confirmed) factor = Math.min(1.0, factor + 0.1); // §1.3: Navamsa-confirmed → results certain
  return {
    factor: Math.max(0, Math.min(1, factor)),
    navamsa_confirmed,
    combust: combust_list.sort(),
    war_defeated: war_list.sort(),
  };
}

// ── Per-chart memo ──────────────────────────────────────────────────────────────────────────
// Formation (raja/dhana/pmp), chart-wide bhanga, and the war-loser set are pure functions of the
// natal chart alone, but score_yoga_for_event runs PER EVENT inside kp_scoring's event loop —
// which itself runs per candidate-second. Recomputing the ~9 relationship()/drishti sweeps and
// the 10-pair war scan for every event repeated identical work thousands of times per scan
// (found by the 2026-07-02 review), so the chart-invariant pieces are computed once per chart
// object (same WeakMap pattern as strength_scoring's _war_cache).
interface ChartYogaState {
  asc_sign: number;
  raja: Map<string, number>;
  named: { forming: Map<string, number>; names: string[] };
  dhana: Map<string, number>;
  pmp: Set<string>;
  pmp_weak_luminaries: boolean;
  nabhasha: { status: number; wealth: number; names: string[] };
  bhanga: string | null;
  war_defeated: Set<string>;
}
const _chart_yoga_cache = new WeakMap<object, ChartYogaState>();

function _chart_yoga_state(chart: any): ChartYogaState {
  let state = _chart_yoga_cache.get(chart);
  if (state === undefined) {
    const asc_sign = mod(Math.floor(chart.ascendant_longitude / 30), 12);
    const war_defeated = new Set<string>();
    // ≤1° separation, cusp-safe — helpers.ts resolves the victor (behind along the short arc).
    for (const war of compute_graha_yuddha(chart?.planets ?? {})) {
      war_defeated.add(String(war.defeated));
    }
    state = {
      asc_sign,
      raja: raja_forming(chart, asc_sign),
      named: named_status_forming(chart, asc_sign),
      dhana: dhana_forming(chart, asc_sign),
      pmp: pmp_forming(chart, asc_sign),
      pmp_weak_luminaries: pmp_luminaries_weak(chart),
      nabhasha: nabhasha_forming(chart, asc_sign),
      bhanga: yoga_bhanga(chart, asc_sign),
      war_defeated,
    };
    _chart_yoga_cache.set(chart, state);
  }
  return state;
}

/**
 * Yoga confirmation for one event. Returns [score (50–100) | null, detail]. null when the
 * category has no Vol_07 yoga or no yoga is formed under this lagna (no component — never a
 * penalty). A Yoga-Bhanga-cancelled yoga returns exactly the neutral 50 (never below) with the
 * cancellation reason exposed in the detail object.
 */
export function score_yoga_for_event(
  natal_chart: any,
  category: string,
  event_dt: PlainDateTime,
  birth_dt: PlainDateTime,
  dasha_info: Record<string, number>,
  timezone_offset: number,
): [number | null, Record<string, any>] {
  const wants_raja = RAJA_CATEGORIES.has(category);
  const wants_dhana = DHANA_CATEGORIES.has(category);
  const wants_pmp = PMP_CATEGORIES.has(category);
  if (!wants_raja && !wants_dhana && !wants_pmp) return [null, {}];

  // Formation + bhanga are chart-invariant — computed once per chart (see _chart_yoga_state).
  const state = _chart_yoga_state(natal_chart);
  const asc_sign = state.asc_sign;

  // Assemble the routes SEPARATELY (2026-07-02 review: no teardown-and-rebuild) — the
  // chart-wide bhanga applies only to the raja/dhana (+ named) routes, never to
  // Panchamahapurusha or to the permanently-active Nabhasha patterns.
  const rd_forming = new Map<string, number>(); // planet → route grade (strongest route wins)
  const rd_types: string[] = [];
  const named_names: string[] = [];
  const combust_exempt = new Set<string>();
  if (wants_raja && state.raja.size > 0) {
    // Per-planet formation grades (1.0 full; 0.5 for the lordship-only Scorpio luminaries).
    for (const [p, g] of state.raja) rd_forming.set(p, Math.max(rd_forming.get(p) ?? 0, g));
    rd_types.push("raja");
  }
  // Named status/Raja yogas (Gaja Kesari, Adhi, Sunapha/Anapha/Durudhara, Amala, Lakshmi,
  // exalted-planet tiers, benefics-in-angles, exalted 10th lord — see named_status_forming)
  // back the same status categories and ride the raja/dhana bhanga scope.
  if (wants_raja && state.named.forming.size > 0) {
    for (const [p, g] of state.named.forming) rd_forming.set(p, Math.max(rd_forming.get(p) ?? 0, g));
    rd_types.push("named_raja");
    named_names.push(...state.named.names);
  }
  if (wants_dhana && state.dhana.size > 0) {
    for (const [p, grade] of state.dhana) rd_forming.set(p, Math.max(rd_forming.get(p) ?? 0, grade));
    rd_types.push("dhana");
    // 07_dhana "11th Lord in Kendra (Even if Combust)": combustion explicitly does NOT cancel
    // THIS form, so that planet is exempted from the combustion nil in strength_factor.
    const lord11 = lord_of(asc_sign, 11);
    const h11 = house_of(natal_chart, asc_sign, lord11);
    if (state.dhana.has(lord11) && h11 !== null && KENDRA_HOUSES_FULL.includes(h11)) combust_exempt.add(lord11);
  }
  const pmp_planets = wants_pmp ? state.pmp : new Set<string>();
  // 07_panchamahapurusha caveat: "if Sun AND Moon are both weak, the native will NOT attain
  // kinghood" — the PMP formation stays computed and audited but does not lift the score.
  const pmp_damped = pmp_planets.size > 0 && state.pmp_weak_luminaries;
  const pmp_live = pmp_damped ? new Set<string>() : pmp_planets;
  // Nabhasha routes: status patterns back the Raja/status categories, wealth patterns the
  // Dhana categories (see nabhasha_forming for the doc quotes and the dasha-bypass rationale).
  let nab_grade = 0;
  if (wants_raja && state.nabhasha.status > 0) nab_grade = Math.max(nab_grade, state.nabhasha.status);
  if (wants_dhana && state.nabhasha.wealth > 0) nab_grade = Math.max(nab_grade, state.nabhasha.wealth);

  if (rd_forming.size === 0 && pmp_planets.size === 0 && nab_grade === 0) return [null, {}];

  // Yoga Bhanga: chart-wide cancellation kills the Raja/Dhana confirmation — the yoga is formed
  // but "cancelled or severely reduced", so that route reads exactly neutral (50, never below).
  // SCOPE (07_yoga_fundamentals_and_cancellation: the conditions apply "alongside Raja or Wealth
  // Yogas"): Panchamahapurusha is neither — it carries its OWN cancellation list (combustion /
  // planetary war, already nil'd by strength_factor) — so a PMP formation SURVIVES chart-wide
  // bhanga (scoped 2026-07-02); Nabhasha patterns are "permanently active" distribution yogas
  // and survive it too. Only the raja/dhana/named routes are dropped.
  const bhanga = rd_types.length > 0 ? state.bhanga : null;
  if (bhanga !== null && pmp_live.size === 0 && nab_grade === 0) {
    return [
      50.0,
      {
        yoga_types: rd_types,
        forming_planets: [...rd_forming.keys()].sort(),
        cancelled: true,
        cancellation: bhanga,
        ...(pmp_damped ? { pmp_luminaries_weak: true } : {}),
      },
    ];
  }

  // Select the LIVE routes: raja/dhana/named unless bhanga-cancelled, plus any live PMP.
  const forming = new Map<string, number>();
  const yoga_types: string[] = [];
  if (bhanga === null && rd_forming.size > 0) {
    for (const [p, g] of rd_forming) forming.set(p, g);
    yoga_types.push(...rd_types);
  }
  for (const p of pmp_live) forming.set(p, Math.max(forming.get(p) ?? 0, 1.0));
  if (pmp_live.size > 0) yoga_types.push("panchamahapurusha");
  if (nab_grade > 0) yoga_types.push("nabhasha");
  const live_exempt = bhanga === null ? combust_exempt : new Set<string>();

  const { factor, navamsa_confirmed, combust, war_defeated } = strength_factor(natal_chart, forming, live_exempt);

  // §1.2.3 / §1.3 Timing: the yoga manifests in the Dasha/Antardasha of a yoga-forming planet.
  // DORMANT = 0.0 (score 50, exactly neutral): the doc is explicit that a yoga "not yet activated
  // by Dasha is dormant — it does not produce results" and is "background influence only" — so a
  // formed-but-dormant yoga must NOT confirm an event dated OUTSIDE its dasha. The 0.0 (rather
  // than a ≤0.1 token) is chosen because this component judges the EVENT DATE: crediting dormant
  // formation would reward the lagna on every event regardless of timing, diluting exactly the
  // dasha discrimination this component exists to add.
  const dasha = get_current_dasha(event_dt, birth_dt, dasha_info, timezone_offset);
  const maha = String(dasha.mahadasha_lord ?? "").toLowerCase();
  const antar = String(dasha.antardasha_lord ?? "").toLowerCase();
  let activation = 0.0;
  let activation_level = "dormant";
  if (forming.has(maha)) { activation = 1.0; activation_level = "mahadasha"; }
  else if (forming.has(antar)) { activation = 0.7; activation_level = "antardasha"; }

  // Light confirmation, mirroring transit_scoring: 50 (neutral) → 100 (strong, active).
  let score = forming.size > 0 ? 50.0 + 50.0 * factor * activation : 50.0;
  // Nabhasha patterns BYPASS the dasha gate (07_nabhasha_yogas.md: "permanently active, not
  // only during specific Dashas") — their confirmation is graded by the distribution's planet
  // dignities alone and can lift a dormant (or bhanga-cancelled) main route, never lower it.
  let nab_factor = 0;
  if (nab_grade > 0) {
    const nab_forming = new Map<string, number>(PLANETS.map((p) => [p, nab_grade]));
    // "mean": the 7-planet distribution is ONE yoga — see strength_factor's aggregation note.
    nab_factor = strength_factor(natal_chart, nab_forming, new Set(), "mean").factor;
    score = Math.max(score, 50.0 + 50.0 * nab_factor);
  }
  score = Math.max(50.0, Math.min(100.0, score));
  return [
    score,
    {
      yoga_types,
      forming_planets: [...forming.keys()].sort(),
      strength_factor: pythonRound(factor, 3),
      navamsa_confirmed,
      combust_planets: combust,
      war_defeated_planets: war_defeated,
      activation: activation_level,
      maha_lord: maha,
      antar_lord: antar,
      // Named-yoga and Nabhasha audit extensions (only when those routes are live).
      ...(bhanga === null && named_names.length > 0 ? { named_yogas: [...named_names] } : {}),
      ...(nab_grade > 0
        ? { nabhasha_yogas: [...state.nabhasha.names], nabhasha_dasha_bypass: true, nabhasha_factor: pythonRound(nab_factor, 3) }
        : {}),
      ...(pmp_damped ? { pmp_luminaries_weak: true } : {}),
      // When chart-wide bhanga cancelled the raja/dhana routes but a PMP/Nabhasha formation
      // survived, surface the cancellation so the audit trail stays honest.
      ...(bhanga !== null ? { raja_dhana_cancelled: bhanga } : {}),
    },
  ];
}
